import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { LocalDate } from "@js-joda/core";
import { useTranslation } from "react-i18next";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import {
  type AnalyticsPeriod,
  getWeekBounds,
} from "./analytics.operatingHours";
import {
  Backdrop,
  Pane,
  PaneHeader,
  PaneTitle,
  CloseBtn,
  PaneDescription,
  PaneFooter,
  EmptyNote,
  DeleteBtn,
  AddSection,
  AddSectionTitle,
  FormField,
  FormLabel,
  FormInput,
  FormRow,
  DatePickerWrapper,
  AddButtonRow,
  EarningsList,
  EarningRow,
  EarningInfo,
  EarningTitle,
  EarningMeta,
} from "./analytics.customEarnings.styles";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomEarning {
  id: string;
  title: string;
  amount: number;
  /** ISO date string "YYYY-MM-DD" — the specific calendar day this earning belongs to */
  date: string;
}

// ─── localStorage helpers ─────────────────────────────────────────────────────

const STORAGE_KEY = "analytics_custom_earnings";

export const loadCustomEarnings = (): CustomEarning[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed))
      return parsed.map(
        (e): CustomEarning => ({
          id: e.id,
          title: e.title,
          amount: e.amount,
          date: e.date,
        }),
      );
  } catch {}
  return [];
};

export const saveCustomEarnings = (earnings: CustomEarning[]): void =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(earnings));

// ─── Period-aware total ───────────────────────────────────────────────────────

/**
 * Returns the total custom earnings active for the selected analytics period.
 *  - Day view  : earnings whose date equals selectedDate.
 *  - Week view : earnings whose date falls within the Mon–Sun week of selectedDate.
 *  - Month view: earnings whose date falls within the calendar month of selectedDate.
 *  - Year view : earnings whose date falls within the calendar year of selectedDate.
 */
export const getCustomEarningsTotal = (
  earnings: CustomEarning[],
  period: AnalyticsPeriod,
  selectedDate: LocalDate,
): number => {
  if (period === "day") {
    const dateStr = selectedDate.toString();
    return earnings
      .filter((e) => e.date === dateStr)
      .reduce((sum, e) => sum + e.amount, 0);
  }
  if (period === "week") {
    const { weekStart, weekEnd } = getWeekBounds(selectedDate);
    return earnings
      .filter((e) => {
        const d = LocalDate.parse(e.date);
        return !d.isBefore(weekStart) && !d.isAfter(weekEnd);
      })
      .reduce((sum, e) => sum + e.amount, 0);
  }
  if (period === "month") {
    return earnings
      .filter((e) => {
        const d = LocalDate.parse(e.date);
        return (
          d.year() === selectedDate.year() &&
          d.monthValue() === selectedDate.monthValue()
        );
      })
      .reduce((sum, e) => sum + e.amount, 0);
  }
  // year
  return earnings
    .filter((e) => LocalDate.parse(e.date).year() === selectedDate.year())
    .reduce((sum, e) => sum + e.amount, 0);
};

// ─── Side pane ────────────────────────────────────────────────────────────────

interface CustomEarningsPaneProps {
  initial: CustomEarning[];
  open: boolean;
  /** Pre-fills the date picker when adding a new earning */
  selectedDate: LocalDate;
  onSave: (earnings: CustomEarning[]) => void;
  onClose: () => void;
}

const generateId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const CustomEarningsPane = (props: CustomEarningsPaneProps) => {
  const { t } = useTranslation();
  const [earnings, setEarnings] = useState<CustomEarning[]>(props.initial);
  const [newTitle, setNewTitle] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [newDate, setNewDate] = useState<LocalDate>(props.selectedDate);

  const wasOpen = useRef(false);
  useEffect(() => {
    if (props.open && !wasOpen.current) {
      setEarnings(props.initial);
      setNewTitle("");
      setNewAmount("");
      setNewDate(props.selectedDate);
    }
    wasOpen.current = props.open;
  }, [props.open, props.initial, props.selectedDate]);

  const handleAdd = () => {
    const amount = parseFloat(newAmount.replace(",", "."));
    if (!newTitle.trim() || !Number.isFinite(amount) || amount <= 0) return;
    setEarnings((prev) => [
      ...prev,
      {
        id: generateId(),
        title: newTitle.trim(),
        amount,
        date: newDate.toString(),
      },
    ]);
    setNewTitle("");
    setNewAmount("");
    // keep the date — user likely adds multiple earnings for the same day
  };

  const handleDelete = (id: string) =>
    setEarnings((prev) => prev.filter((e) => e.id !== id));

  const handleSave = () => {
    saveCustomEarnings(earnings);
    props.onSave(earnings);
  };

  return createPortal(
    <>
      <Backdrop $open={props.open} onClick={props.onClose} />
      <Pane $open={props.open}>
        <PaneHeader>
          <PaneTitle>{t("CompanyAnalytics.CustomEarnings")}</PaneTitle>
          <CloseBtn onClick={props.onClose} aria-label="Close">
            ✕
          </CloseBtn>
        </PaneHeader>
        <PaneDescription>
          {t("CompanyAnalytics.CustomEarningsDescription")}
        </PaneDescription>

        {/* ── Existing earnings list ── */}
        <EarningsList>
          {earnings.length === 0 ? (
            <EmptyNote>{t("CompanyAnalytics.NoCustomEarnings")}</EmptyNote>
          ) : (
            earnings.map((earning) => (
              <EarningRow key={earning.id}>
                <EarningInfo>
                  <EarningTitle>{earning.title}</EarningTitle>
                  <EarningMeta>
                    {Math.round(earning.amount).toLocaleString("sv-SE")} kr
                    {" · "}
                    {(() => {
                      const d = LocalDate.parse(earning.date);
                      return `${String(d.dayOfMonth()).padStart(2, "0")}/${String(d.monthValue()).padStart(2, "0")}/${d.year()}`;
                    })()}
                  </EarningMeta>
                </EarningInfo>
                <DeleteBtn
                  onClick={() => handleDelete(earning.id)}
                  aria-label={t("CompanyAnalytics.DeleteEarning")}
                >
                  ✕
                </DeleteBtn>
              </EarningRow>
            ))
          )}
        </EarningsList>

        {/* ── Add new earning form ── */}
        <AddSection>
          <AddSectionTitle>{t("CompanyAnalytics.AddEarning")}</AddSectionTitle>
          <FormField>
            <FormLabel>{t("CompanyAnalytics.EarningTitle")}</FormLabel>
            <FormInput
              type="text"
              value={newTitle}
              placeholder={t("CompanyAnalytics.EarningTitlePlaceholder")}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
          </FormField>
          <FormRow>
            <FormField>
              <FormLabel>{t("CompanyAnalytics.CostAmount")}</FormLabel>
              <FormInput
                type="number"
                value={newAmount}
                placeholder="0"
                min="0"
                onChange={(e) => setNewAmount(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              />
            </FormField>
            <FormField>
              <FormLabel>{t("CompanyAnalytics.EarningDate")}</FormLabel>
              <DatePickerWrapper>
                <DatePicker
                  value={newDate}
                  onChange={(date) => {
                    if (date) setNewDate(date);
                  }}
                  size="compact"
                  dateFormat="DD/MM/YYYY"
                />
              </DatePickerWrapper>
            </FormField>
          </FormRow>
          <AddButtonRow>
            <SecondaryButton
              label={t("CompanyAnalytics.AddEarning")}
              onClick={handleAdd}
            />
          </AddButtonRow>
        </AddSection>

        <PaneFooter>
          <SecondaryButton
            label={t("CompanyAnalytics.Cancel")}
            onClick={props.onClose}
          />
          <PrimaryButton
            label={t("CompanyAnalytics.Save")}
            onClick={handleSave}
          />
        </PaneFooter>
      </Pane>
    </>,
    document.body,
  );
};
